// Мини-панель над выделенным текстом: стиль абзаца, полужирный/курсив/подчёркнутый, индексы, код, ссылка,
// очистка, ссылка на источник и перекрёстная ссылка. Появляется после выделения мышью (как в Word; после выделения
// клавиатурой - нет). Панель сначала полупрозрачная и НЕ перехватывает щелчки (они проходят в текст под ней);
// активной она становится, только если задержать над ней указатель (~0,25 с). Любой щелчок вне панели её прячет.
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NodeSelection } from '@tiptap/pm/state';
import { registry } from '../../core/registry';
import { ctx, runCommand } from '../../core/ctx';
import { useApp } from '../../core/store';
import { Icon } from '../../ui/icons';
import { prettyShortcut } from '../../ui/controls';
import { useUi } from '../../ui/uiState';

const MARKS = ['format.bold', 'format.italic', 'format.underline', 'format.superscript', 'format.subscript', 'format.code', 'format.link', 'format.clear'];
const EXTRA = ['refs.cite', 'refs.xref', 'refs.footnote'];
const STYLES: [string, string][] = [['block.paragraph', 'Текст'], ['block.h1', 'Раздел'], ['block.h2', 'Подраздел'], ['block.h3', 'Пункт'],
  ['list.dash', 'Перечисление –'], ['list.letter', 'Перечисление а)'], ['list.digit', 'Перечисление 1)']];

export function BubbleBar() {
  const editor = useApp((s) => s.editor);
  const focus = useUi((s) => s.focusMode);
  const [pos, setPos] = useState<{ x: number; y: number; below: boolean } | null>(null);
  const [, bump] = useState(0);
  const mouseDown = useRef(false);
  const keySel = useRef(-1e9);                 // время последней клавиши выделения (Shift+стрелки, Ctrl+A …)
  const barRef = useRef<HTMLDivElement | null>(null);
  const [armed, setArmed] = useState(false);
  const [styleOpen, setStyleOpen] = useState(false);

  useEffect(() => {
    if (!editor) return;
    let raf = 0;
    const calc = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (editor.isDestroyed) return;
        const { state, view } = editor;
        const sel = state.selection;
        const inCode = sel.$from.parent.type.spec.code;
        if (sel.empty || sel instanceof NodeSelection || !view.hasFocus() || mouseDown.current || inCode
          || performance.now() - keySel.current < 800
          || state.doc.textBetween(sel.from, sel.to, ' ').trim() === '') { setPos(null); setStyleOpen(false); setArmed(false); return; }
        const a = view.coordsAtPos(sel.from);
        const b = view.coordsAtPos(sel.to);
        const top = Math.min(a.top, b.top);
        const x = (Math.min(a.left, b.left) + Math.max(a.right, b.right)) / 2;
        const below = top < 150;
        setPos({ x, y: below ? Math.max(a.bottom, b.bottom) + 8 : top - 8, below });
        bump((k) => k + 1);
      });
    };
    const md = () => { mouseDown.current = true; keySel.current = -1e9; setPos(null); setArmed(false); };
    // любой щелчок вне панели (поля листа, серый фон, лента) - спрятать
    const docMd = (e: MouseEvent) => { if (!barRef.current?.contains(e.target as Node)) { setPos(null); setArmed(false); } };
    const kd = (e: KeyboardEvent) => {
      if (e.shiftKey || ((e.ctrlKey || e.metaKey) && (e.key === 'a' || e.key === 'A' || e.code === 'KeyA'))) keySel.current = performance.now();
      else if (!['Control', 'Shift', 'Alt', 'Meta'].includes(e.key)) { keySel.current = performance.now(); setPos(null); }
    };
    const mu = () => { if (mouseDown.current) { mouseDown.current = false; setTimeout(calc, 0); } };
    const blur = () => setTimeout(() => { if (!editor.isDestroyed && !editor.view.hasFocus()) setPos(null); }, 120);
    editor.on('selectionUpdate', calc);
    editor.on('transaction', calc);
    editor.view.dom.addEventListener('mousedown', md);
    editor.view.dom.addEventListener('keydown', kd, true);
    document.addEventListener('mousedown', docMd, true);
    window.addEventListener('mouseup', mu);
    editor.view.dom.addEventListener('blur', blur);
    const sc = () => setPos(null);
    const canvas = document.querySelector('.canvas');
    canvas?.addEventListener('scroll', sc, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      editor.off('selectionUpdate', calc);
      editor.off('transaction', calc);
      if (!editor.isDestroyed) { editor.view.dom.removeEventListener('mousedown', md); editor.view.dom.removeEventListener('blur', blur); editor.view.dom.removeEventListener('keydown', kd, true); }
      document.removeEventListener('mousedown', docMd, true);
      window.removeEventListener('mouseup', mu);
      canvas?.removeEventListener('scroll', sc);
    };
  }, [editor]);

  // «взвод» панели: указатель задержался над ней ~0,25 с (проверка по координатам - сама панель щелчки не ловит)
  useEffect(() => {
    if (!pos) return;
    let timer = 0;
    const mv = (e: MouseEvent) => {
      const r = barRef.current?.getBoundingClientRect();
      const inside = !!r && e.clientX >= r.left - 2 && e.clientX <= r.right + 2 && e.clientY >= r.top - 2 && e.clientY <= r.bottom + 2;
      if (inside) { if (!timer) timer = window.setTimeout(() => setArmed(true), 250); }
      else { if (timer) { clearTimeout(timer); timer = 0; } setArmed(false); }
    };
    window.addEventListener('mousemove', mv, true);
    return () => { window.removeEventListener('mousemove', mv, true); if (timer) clearTimeout(timer); };
  }, [pos]);

  if (!editor || !pos || focus) return null;
  const btn = (id: string) => {
    const c = registry.commands.get(id);
    if (!c) return null;
    const active = c.isActive ? c.isActive(ctx) : false;
    return (
      <button key={id} type="button" className={'bb-btn' + (active ? ' is-active' : '')} aria-pressed={active} aria-label={c.title}
        data-tip={c.title} data-tip-key={c.shortcut ? prettyShortcut(c.shortcut) : undefined} data-testid={'bb-' + id}
        onMouseDown={(e) => e.preventDefault()} onClick={() => runCommand(id)}><Icon name={c.icon} size={15} /></button>
    );
  };
  const curStyle = STYLES.find(([id]) => registry.commands.get(id)?.isActive?.(ctx))?.[1] || 'Стиль';
  return createPortal(
    <div ref={barRef} className={'bubble-bar' + (pos.below ? ' is-below' : '') + (armed || styleOpen ? ' is-armed' : '')} style={{ left: pos.x, top: pos.y }} role="toolbar"
      aria-label="Оформление выделенного" data-testid="bubble-bar" onMouseDown={(e) => e.preventDefault()}>
      <div className="bb-style">
        <button type="button" className="bb-style-btn" onClick={() => setStyleOpen(!styleOpen)} data-tip="Стиль абзаца">
          <span>{curStyle}</span><Icon name="down" size={12} />
        </button>
        {styleOpen && (
          <div className="bb-style-menu">
            {STYLES.map(([id, label]) => (
              <button key={id} type="button" className={'menu-item' + (registry.commands.get(id)?.isActive?.(ctx) ? ' is-checked' : '')}
                onClick={() => { setStyleOpen(false); runCommand(id); }}>
                <span className="menu-label">{label}</span>
                <span className="menu-key">{prettyShortcut(registry.commands.get(id)?.shortcut)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <span className="bb-sep" />
      {MARKS.map(btn)}
      <span className="bb-sep" />
      {EXTRA.map(btn)}
    </div>, document.body);
}
